// ============================================
// APPLICATION - Permisos Hooks
// ============================================
// Módulo genérico. Hoy solo consumido por caja, pero no depende de nada de
// caja — cualquier módulo futuro con su propio PermisoXxx reusa estos mismos
// hooks contra el catálogo/endpoints de permisos.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { permisosRepository } from '../../infrastructure/http/permisos.repository';
import { queryKeys } from '../query-keys';
import type { PermisoCaja } from '../../domain/dtos';

/** Catálogo de permisos disponibles, opcionalmente filtrado por módulo (solo propietario) */
export const useCatalogoPermisos = (moduloCodigo?: string) => {
  return useQuery({
    queryKey: queryKeys.permisos.catalogo(moduloCodigo),
    queryFn: () => permisosRepository.obtenerCatalogo(moduloCodigo),
    staleTime: 10 * 60 * 1000,
  });
};

/** Permisos efectivos del usuario autenticado actual — para gatear la propia UI */
export const useMisPermisos = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.permisos.misPermisos(),
    queryFn: () => permisosRepository.obtenerMisPermisos(),
    staleTime: 60 * 1000,
    enabled,
  });
};

/** Permisos actuales de un vendedor puntual del almacén (solo propietario) */
export const usePermisosDeUsuario = (usuarioId: number | undefined) => {
  return useQuery({
    queryKey: queryKeys.permisos.deUsuario(usuarioId ?? 0),
    queryFn: () => permisosRepository.obtenerPermisosDeUsuario(usuarioId as number),
    enabled: !!usuarioId,
    staleTime: 15 * 1000,
  });
};

/** Otorgar un permiso a un vendedor (solo propietario) */
export const useAsignarPermiso = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, permiso }: { usuarioId: number; permiso: PermisoCaja }) =>
      permisosRepository.asignarPermiso(usuarioId, permiso),
    onSuccess: async (_data, { usuarioId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.permisos.deUsuario(usuarioId) });
    },
  });
};

/** Revocar un permiso a un vendedor (solo propietario) */
export const useRevocarPermiso = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, permiso }: { usuarioId: number; permiso: PermisoCaja }) =>
      permisosRepository.revocarPermiso(usuarioId, permiso),
    onSuccess: async (_data, { usuarioId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.permisos.deUsuario(usuarioId) });
    },
  });
};
